import { AddCircleOutline, CheckCircleOutline } from '@mui/icons-material'
import { Box, Grid } from '@mui/material'
import { Context, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGenerateDeviationPDF } from 'src/api/deviation'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { permissionValue } from '../../../enums/permissionValue'
import { IDeviation } from '../../../generated-types/deviation'
import { DeviationContextSidebar } from './detail/DeviationContextSidebar'
import { hasDeviationPlacementContent } from './detail/DeviationContextSidebar.action'
import { DeviationDetailsPanel } from './detail/DeviationDetailsPanel'
import { DeviationHeaderBar } from './detail/DeviationHeaderBar'
import { DeviationKeyFacts } from './detail/DeviationKeyFacts'
import { DeviationSectionCard } from './detail/DeviationSectionCard'
import DeviationDocuments, { DeviationDocumentsHandle } from './DeviationDocuments'
import { DeviationFollowUpTable, DeviationFollowUpTableAdmin } from './DeviationFollowUpTable'

const FOLLOW_UP_WRITE_PERMISSIONS = [
  permissionValue.Locality.Deviation.Write,
  permissionValue.Locality.Task.Update,
  permissionValue.Locality.Task.Write,
  permissionValue.Locality.Service.Update,
  permissionValue.Locality.Service.Write,
  permissionValue.ClientAdminPermission,
  permissionValue.ServiceStation.Write,
  permissionValue.ServiceStation.Update,
]

const DOCUMENT_WRITE_PERMISSIONS = [
  permissionValue.Locality.Deviation.Write,
  permissionValue.Locality.Deviation.Update,
  permissionValue.ClientAdminPermission,
  permissionValue.ServiceStation.Write,
  permissionValue.ServiceStation.Update,
  permissionValue.Locality.MainComponent.Deviation.Write,
]

export const GenericDeviationContainer: React.FC<{
  customerId?: EntityId
  localityId?: EntityId
  deviationId: EntityId
  data?: IDeviation
  updateDeviation: (deviation: IDeviation) => void
  onAdd: (defaultStatus?: boolean) => void
  isAdminLayout?: boolean
  context?: Context<any> | null
}> = ({ customerId, localityId, deviationId, data, updateDeviation, onAdd, isAdminLayout, context }) => {
  const { t } = useTranslation(['deviation', 'common', 'document', 'overall-reports'])
  const { mutateAsync: downloadPDF, isLoading: isDownloadingPDF } = useGenerateDeviationPDF(
    customerId as EntityId,
    deviationId,
  )
  const documentsRef = useRef<DeviationDocumentsHandle>(null)

  const closedBy = useMemo(() => {
    if (data?.status) {
      const closedFollowup = data?.deviationFollowUps?.find(df => df.status)
      const createdUser = closedFollowup?.createdByUser
      if (createdUser) {
        return getUserDetail(createdUser, UserDetail.FullName)
      }
    }
    return ''
  }, [data])

  // Mirror the deviation type-flag derivation used elsewhere for the header meta line.
  const typeLabels = useMemo(() => {
    const labels: string[] = []
    if (data?.visualDeviation) labels.push('visualDeviation')
    if (data?.abnormalWear) labels.push('abnormalWear')
    if (data?.measuredDeviation) labels.push('measuredDeviation')
    if (data?.normalWear) labels.push('normalWear')
    return labels
  }, [data?.visualDeviation, data?.abnormalWear, data?.measuredDeviation, data?.normalWear])

  const handleDownloadPDF = async () => {
    if (!data?.id) return
    const res = await downloadPDF({})
    downloadFileLocal(res, `avvik_${data.id}`)
  }

  const hasSidebarContent = hasDeviationPlacementContent(data, customerId, localityId)

  return (
    <Box sx={{ background: '#F1F5F7', display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
      <DeviationHeaderBar
        data={data}
        typeLabels={typeLabels}
        closedBy={closedBy}
        isAdminLayout={isAdminLayout}
        customerId={customerId}
        localityId={localityId}
        context={context}
        isDownloadingPDF={isDownloadingPDF}
        onDownloadPDF={handleDownloadPDF}
        onEdit={deviation => updateDeviation(deviation)}
      />

      <DeviationKeyFacts deviation={data} />

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={hasSidebarContent ? 8 : 12}>
          <DeviationDetailsPanel deviation={data} spreadLayout={!hasSidebarContent} />
        </Grid>
        {hasSidebarContent && (
          <Grid item xs={12} md={4}>
            <DeviationContextSidebar deviation={data} customerId={customerId} localityId={localityId} />
          </Grid>
        )}
      </Grid>

      {/* Oppfølginger */}
      <DeviationSectionCard
        title={t('deviationFollowUps', { ns: 'deviation' })}
        noBodyPadding
        actions={
          <>
            <GridActionButton
              buttonText={t('follow-up', { ns: 'deviation' })}
              buttonIcon={<AddCircleOutline />}
              onClick={() => onAdd(false)}
              disabled={data?.status}
              context={context}
              permissionBinaryValues={isAdminLayout ? undefined : FOLLOW_UP_WRITE_PERMISSIONS}
              variant="outlined"
              shadowWeight={null}
              width="auto"
              size="small"
            />
            <GridActionButton
              buttonText={t('closeDeviation', { ns: 'deviation' })}
              buttonIcon={<CheckCircleOutline />}
              onClick={() => onAdd(true)}
              disabled={data?.status}
              context={context}
              permissionBinaryValues={isAdminLayout ? undefined : FOLLOW_UP_WRITE_PERMISSIONS}
              variant="outlined"
              shadowWeight={null}
              width="auto"
              size="small"
            />
          </>
        }
      >
        {!isAdminLayout ? (
          <DeviationFollowUpTable context={context} deviation={data as IDeviation} />
        ) : (
          data && <DeviationFollowUpTableAdmin context={context} deviation={data as IDeviation} />
        )}
      </DeviationSectionCard>

      {/* Vedlegg — non-admin only, same condition as before */}
      {!isAdminLayout && (
        <DeviationSectionCard
          title={t('attachments&reports', { ns: 'deviation' })}
          noBodyPadding
          actions={
            <>
              <GridActionButton
                buttonText={t('new-form', { ns: 'overall-reports' })}
                buttonIcon={<AddCircleOutline />}
                onClick={() => documentsRef.current?.openAddForm()}
                disabled={data?.status}
                data-cy="addNewForm"
                context={context}
                permissionBinaryValues={DOCUMENT_WRITE_PERMISSIONS}
                variant="outlined"
                shadowWeight={null}
                width="auto"
                size="small"
              />
              <GridActionButton
                buttonText={t('new-attachments', { ns: 'document' })}
                buttonIcon={<AddCircleOutline />}
                onClick={() => documentsRef.current?.openAddAttachment()}
                disabled={data?.status}
                context={context}
                permissionBinaryValues={DOCUMENT_WRITE_PERMISSIONS}
                variant="outlined"
                shadowWeight={null}
                width="auto"
                size="small"
              />
            </>
          }
        >
          <Box sx={{ p: '16px' }}>
            <DeviationDocuments
              ref={documentsRef}
              customerId={customerId as EntityId}
              localityId={localityId as EntityId}
              deviationId={deviationId}
              isDisabled={data?.status}
              context={context}
            />
          </Box>
        </DeviationSectionCard>
      )}
    </Box>
  )
}
